'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgTarget2 } from '@common/icon';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';
import { AuthMutation, MEMBER_JOIN_STATUS } from '@/api';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ROUTES } from '@/constants';
import { DEFAULT_LOCATION } from '@/constants/geo';
import { useLocation } from '@/hooks/useLocation';
import { useLocationConsent } from '@/hooks/useLocationConsent';
import { useAuthStore } from '@/stores/AuthStore';
const enabledPathPrefixes = [ROUTES.MY.pathname, ROUTES.COMMUNITY.pathname, ROUTES.RECOMMEND_POLICY_LIST.pathname];
const excludedPaths = [ROUTES.POINT_MARKET_HISTORY.pathname];
const RegionDrawerContent = () => {
  const $ = _c(19);
  const queryClient = useQueryClient();
  const {
    isConsented
  } = useLocationConsent();
  const location = useLocation();
  const {
    toastError
  } = useToast();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = AuthMutation["post:/wello/v2/auth/member-filter/region"]();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== queryClient) {
    t1 = () => {
      queryClient.invalidateQueries();
    };
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== toastError) {
    t2 = error => toastError({
      error
    });
    $[3] = toastError;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      ...t0,
      onSuccess: t1,
      onError: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const postRegionDataMutation = useMutation(t3);
  if (isConsented === undefined) {
    return null;
  }
  let t4;
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      backdrop: "z-50",
      drawer: "z-50"
    };
    t5 = <h3 className="typo-body2-b mb-8 text-Gray-900">지역을 선택해 주세요</h3>;
    t6 = <p className="typo-body4-r mb-16 text-Gray-700">선택된 지역의 정책과 게시글을 볼 수 있어요.</p>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ul className="pl-18 text-Gray-600 typo-caption1"><li className="list-disc">지역 설정은{" "}<span className="text-Gray-black">{"\uB9DE\uCDA4\uC815\uCC45 > \uB9DE\uCDA4\uC815\uBCF4 \uC218\uC815 > \uC9C0\uC5ED"}</span>에서 언제든지 수정할 수 있어요.</li><li className="list-disc">해외 접속 등 지역 정보 확인이 어려운 경우에는 '서울시 관악구'로 기본 설정돼요.</li></ul>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = location.isLoading || postRegionDataMutation.isPending;
  let t9;
  if ($[12] !== location || $[13] !== postRegionDataMutation) {
    t9 = async () => {
      const {
        data
      } = await location.refetch();
      const codeRegion = data?.codeRegion ?? DEFAULT_LOCATION.codeRegion;
      const codeSubRegion = data?.codeSubRegion ?? DEFAULT_LOCATION.codeSubRegion;
      postRegionDataMutation.mutate({
        body: {
          codeRegion,
          codeSubRegion
        }
      });
    };
    $[12] = location;
    $[13] = postRegionDataMutation;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SvgTarget2 className="mr-6 size-16" name={"\uD604\uC7AC \uC704\uCE58"} />;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t8 || $[17] !== t9) {
    t11 = <ResponsiveDialog opened={true} drawerType="bottom" className={t4}>{t5}{t6}{t7}<Button className="mt-20 w-full" loading={t8} onClick={t9}>{t10}현재 위치로 선택하기</Button></ResponsiveDialog>;
    $[16] = t8;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  return t11;
};
export const RegionDrawer = () => {
  const $ = _c(1);
  const [isLogin, myInfo] = useAuthStore(useShallow(_temp));
  const pathname = usePathname();
  const needRegionData = myInfo?.memberJoinStatus === MEMBER_JOIN_STATUS.EMPTY_FILTER && !(myInfo.codeRegion && myInfo.codeSubRegion);
  const isEnabledPathname = !!pathname && (pathname === ROUTES.HOME.pathname || enabledPathPrefixes.some(path_0 => pathname.startsWith(path_0))) && !excludedPaths.some(path => pathname.startsWith(path));
  if (!isLogin || !needRegionData || !isEnabledPathname) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <RegionDrawerContent />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(state) {
  return [state.isLogin, state.myInfo];
}